/**
 * @file Authentication pages — pages/Auth/login/AdminLoginPage.tsx.
 */

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosClient from "../../../api/axiosClient";
import { useAuth } from "../../../context/AuthContext";
import "./LoginPage.css";
import "../../Landing/LandingPage.css";

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const res = await axiosClient.post("/auth/login", {
        email,
        password,
        role: "ADMIN",
      });

      login(res.data.token, res.data.user);
      localStorage.setItem("loginSource", "admin");

      // redirect based on role
      if (res.data.user.role === "ADMIN") navigate("/dashboard");
      else navigate("/dashboard");
    } catch (err: any) {
      setError(err.response?.data?.error || "Invalid credentials");
    }
  };

  return (
    <div className="landing-root">
      {/* Top Header */}
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      {/* Navigation Bar */}
      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      {/* Hero Section */}
      <section
        className="landing-hero"
        style={{
          backgroundImage: `
      linear-gradient(to right, rgba(201,201,201,0) 68%, #C7C7C7 68%),
      url(${process.env.PUBLIC_URL}/assets/landing_page_bg.png)
    `,
          backgroundSize: "contain",
          backgroundPosition: "left center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#C7C7C7",
        }}
      >
        <div className="landing-overlay">
          <div className="landing-card">
            <h2>ICoMEC</h2>
            <p>Indian Construction Materials Embodied Carbon Data Base</p>

            <form className="login-form" onSubmit={handleLogin}>
              {/* Email */}
              <input
                type="email"
                placeholder="Enter Email ID"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              {/* Password */}
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                      <path d="M2.71 3.16L21.29 21.74l-1.41 1.41L1.29 4.57l1.42-1.41z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              </div>

              <button type="submit" className="btn primary">Log In</button>

              {error && <p className="error-text">{error}</p>}
            </form>

            <div className="landing-card-footer">
              <h3>CARBSE</h3>
              <p>Center for Advanced Research in Building Science Energy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> ·{" "}
          <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> ·{" "}
          <Link to="/terms-of-use">Terms of Use</Link>
        </p>
        <p>© 2025 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};
/** React component — Admin Login Page. */
export default AdminLoginPage;
